import { Fragment, useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Loader2 } from "lucide-react";
import type { AirportConfig } from "@yb/core";
import { api, type DayReport, type FlightHistoryRow, type Measured } from "../lib/api";
import { FILL_BANDS, bandCounts, bandRange, bandTone, figuresSentence, fillBar, fillByAirline, fillTone, flightsSentence, rowFill, rowFillBasis, shapeDays, summarize, type FillBand } from "../lib/history";
import { AirlineMark, Direction } from "./AirlineMark";
import { FlightSeriesChart } from "./FlightSeriesChart";
import { ChangesTable } from "./Changes";
import { FillScaleChips, HowToRead } from "./HowToRead";
import { SecurityTab } from "./HistorySecurity";
import { GatesTab } from "./HistoryGates";
import { BagsTab } from "./HistoryBags";
import { Section } from "./Section";
import { ExportPdfButton, PrintHeader, usePrintStamp } from "./PrintHeader";

/**
 * Looking back over past days: how many flights ran, how punctual they were,
 * how long turns took per airline, which stands were used, and who changed what.
 *
 * Each tab opens with the answer in a sentence, and every section is headed by the
 * question it answers with one line under it saying what the numbers are and where they
 * come from. The page is read by people who did not build it, so the words on it are the
 * airport's ("seats", "through security"), not the feeds' ("pax capacity", "S2"); the CSV
 * keeps the machine names because other systems read that.
 */

const th = "px-2 py-1.5 text-left text-[11px] font-semibold tracking-wide text-ink-3 uppercase";
const td = "px-2 py-1.5 align-middle";
const input = "rounded-md border border-line bg-panel px-2 py-1 text-[12.5px]";
const card = "rounded-xl border border-line p-4";
const tab = (on: boolean) => clsx("rounded-lg px-3 py-1.5 text-[12.5px] font-semibold", on ? "bg-panel-2 text-ink" : "text-ink-2 hover:text-ink");

const pct = (v: number | null) => (v == null ? "—" : `${v}%`);
// A delay below zero is printed as early rather than as a minus sign, which reads as a fault.
const mins = (v: number | null) => (v == null ? "—" : v < 0 ? `${-v} min early` : `${v} min`);
const num = (v: number | null) => (v == null ? "—" : v.toLocaleString());

/** Green from 85% on time, amber from 70, red below: the airport's own words for a day. */
const onTimeTone = (v: number | null) => (v == null ? undefined : v >= 85 ? "text-ok" : v >= 70 ? "text-warn" : "text-bad");
const delayTone = (v: number | null) => (v != null && v > 10 ? "text-warn" : undefined);

export function Reports({
  tz,
  today,
  isAdmin,
  seesCheckpoint = true,
  config,
  onOpenDay,
  printedBy,
}: {
  tz: string;
  today: string;
  isAdmin: boolean;
  /** The name printed on an exported page. */
  printedBy?: string;
  /** The checkpoint's figures are every airline's together, so an airline account does not get the tab. */
  seesCheckpoint?: boolean;
  config?: AirportConfig;
  onOpenDay?: (date: string) => void;
}) {
  const printing = usePrintStamp(tz);
  const [from, setFrom] = useState(() => DateTime.fromISO(today, { zone: tz }).minus({ days: 29 }).toISODate()!);
  const [to, setTo] = useState(today);
  const [page, setPage] = useState<"figures" | "flights" | "security" | "gates" | "bags" | "changes">("figures");
  /**
   * The Bags tab keeps a range of its own, opening on today: bags are read as the day is
   * worked, and a month's totals are a choice to make rather than the first thing seen.
   */
  const [bagsFrom, setBagsFrom] = useState(today);
  const [bagsTo, setBagsTo] = useState(today);
  const bags = page === "bags";
  /** The By flight tab's airline filter, kept here so a click on the Figures tab can set it. */
  const [airline, setAirline] = useState("");

  const figures = useQuery({ queryKey: ["reports", from, to], queryFn: () => api.reports(from, to) });
  const d = figures.data;
  /** Days in the range, inclusive, for "in the last 30 days"; a range ending today is "recent". */
  const days = Math.max(1, Math.round(DateTime.fromISO(to, { zone: tz }).diff(DateTime.fromISO(from, { zone: tz }), "days").days) + 1);
  const place = config?.iata ?? "the airport";
  const sheet = historySheet(page, bags ? bagsFrom : from, bags ? bagsTo : to, tz, config?.iata ?? "");

  return (
    <div className="scroll-thin h-full overflow-auto p-4 print:p-0">
      {/* On paper the tabs and dates give way to a heading that says the same in words. */}
      <div className="hidden print:block">
        <PrintHeader airport={config ? `${config.iata} · ${config.name}` : ""} title={sheet.title} detail={sheet.detail} printed={printing.stamp} by={printedBy} />
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
        <h1 className="mr-2 text-[18px] font-bold">History</h1>
        <button className={tab(page === "figures")} onClick={() => setPage("figures")}>
          Figures
        </button>
        <button className={tab(page === "flights")} onClick={() => setPage("flights")}>
          By flight
        </button>
        {seesCheckpoint && (
          <button className={tab(page === "security")} onClick={() => setPage("security")}>
            Security
          </button>
        )}
        {seesCheckpoint && (
          <button className={tab(page === "gates")} onClick={() => setPage("gates")}>
            Gates
          </button>
        )}
        {seesCheckpoint && (
          <button className={tab(page === "bags")} onClick={() => setPage("bags")}>
            Bags
          </button>
        )}
        {isAdmin && (
          <button className={tab(page === "changes")} onClick={() => setPage("changes")}>
            Changes
          </button>
        )}
        {page !== "changes" && (
          <div className="ml-auto flex items-center gap-2 text-[12px]">
            {bags && (bagsFrom !== today || bagsTo !== today) && (
              <button
                className="rounded-md px-2 py-1 font-semibold text-ink-2 hover:text-ink"
                onClick={() => {
                  setBagsFrom(today);
                  setBagsTo(today);
                }}
              >
                Today
              </button>
            )}
            <label className="flex items-center gap-1">
              From{" "}
              <input
                type="date"
                className={input}
                value={bags ? bagsFrom : from}
                max={bags ? bagsTo : to}
                onChange={(e) => (bags ? setBagsFrom(e.target.value) : setFrom(e.target.value))}
              />
            </label>
            <label className="flex items-center gap-1">
              To{" "}
              <input
                type="date"
                className={input}
                value={bags ? bagsTo : to}
                min={bags ? bagsFrom : from}
                max={today}
                onChange={(e) => (bags ? setBagsTo(e.target.value) : setTo(e.target.value))}
              />
            </label>
          </div>
        )}
        <ExportPdfButton
          className={clsx(page === "changes" && "ml-auto")}
          onClick={() => printing.print(sheet.file)}
          title="Print this tab for the dates chosen, or save it as a PDF to send: choose Save as PDF in the print window"
        />
      </div>

      {page === "changes" ? (
        <ChangesTable tz={tz} today={today} onOpenDay={onOpenDay} />
      ) : page === "flights" ? (
        <FlightTable from={from} to={to} days={days} tz={tz} config={config} airline={airline} onAirline={setAirline} />
      ) : page === "security" && seesCheckpoint ? (
        <SecurityTab from={from} to={to} days={days} tz={tz} today={today} config={config} />
      ) : page === "gates" && seesCheckpoint ? (
        <GatesTab from={from} to={to} place={place} />
      ) : bags && seesCheckpoint ? (
        <BagsTab from={bagsFrom} to={bagsTo} today={today} tz={tz} config={config} />
      ) : figures.isLoading ? (
        <Loader2 className="size-5 animate-spin text-ink-3" />
      ) : !d || d.days.length === 0 ? (
        <>
          <HowToRead id="history.figures">
            <FiguresNotes />
          </HowToRead>
          <p className="text-[13px] text-ink-3">{figuresSentence(null, days, { place, recent: to === today })}</p>
        </>
      ) : (
        <>
          <HowToRead id="history.figures">
            <FiguresNotes />
          </HowToRead>

          <p className="mb-4 max-w-3xl text-[14px] text-ink">{figuresSentence(d.totals, days, { place, recent: to === today })}</p>

          <div className={clsx("mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4", d.totals.screened != null ? "xl:grid-cols-7" : "lg:grid-cols-6")}>
            <Card label="Flights" value={num(d.totals.flights)} sub={`${num(d.totals.arrivals)} arrivals · ${num(d.totals.departures)} departures`} />
            <Card label="On time" value={pct(d.totals.onTimePct)} sub="within 15 min of schedule" tone={onTimeTone(d.totals.onTimePct)} />
            <Card label="Average delay" value={mins(d.totals.avgDelayMin)} sub="against schedule" tone={delayTone(d.totals.avgDelayMin)} />
            <Card label="Cancelled" value={num(d.totals.cancelled)} tone={d.totals.cancelled > 0 ? "text-bad" : undefined} />
            <Card label="Passengers" value={num(d.totals.pax)} sub="airline's figure, where received" />
            {d.totals.screened != null && (
              <Card
                label="Through security"
                value={num(d.totals.screened)}
                sub="boarding passes scanned at the checkpoint"
                onClick={seesCheckpoint ? () => setPage("security") : undefined}
              />
            )}
            <Card label="Plan changes" value={num(d.totals.planVersions)} sub="times a day's plan was changed" />
          </div>

          <Section
            title="How did each day go?"
            caption="One bar per day. Blue is arrivals, green departures, a red cap means cancellations. The line is the share on time, read on the right-hand scale. The table under it is the same figures, newest day first."
          >
            <DaysChart days={d.days} tz={tz} />
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-line">
                  <th className={th}>Date</th>
                  <th className={th}>Flights</th>
                  <th className={th}>Arrivals</th>
                  <th className={th}>Departures</th>
                  <th className={th}>Cancelled</th>
                  <th className={th} title="Share within 15 minutes of schedule">
                    On time
                  </th>
                  <th className={th} title="Minutes late against schedule, averaged; a day that ran early says early">
                    Average delay
                  </th>
                  <th className={th} title="The airline's own figure, where a load report was received">
                    Passengers
                  </th>
                  {d.totals.screened != null && (
                    <th className={th} title="Passengers through the security checkpoint, from its boarding-pass scans">
                      Through security
                    </th>
                  )}
                  <th className={th} title="Times that day's plan was changed">
                    Plan changes
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...d.days].reverse().map((r) => (
                  <tr key={r.date} className="border-b border-line">
                    <td className={clsx(td, "font-semibold")}>{DateTime.fromISO(r.date, { zone: tz }).toFormat("ccc d LLL")}</td>
                    <td className={td}>{r.flights}</td>
                    <td className={td}>{r.arrivals}</td>
                    <td className={td}>{r.departures}</td>
                    <td className={clsx(td, r.cancelled ? "text-bad" : "")}>{r.cancelled || ""}</td>
                    <td className={clsx(td, onTimeTone(r.onTimePct))}>{pct(r.onTimePct)}</td>
                    <td className={clsx(td, delayTone(r.avgDelayMin))}>{mins(r.avgDelayMin)}</td>
                    <td className={td}>{num(r.pax)}</td>
                    {d.totals.screened != null && <td className={td}>{num(r.screened ?? null)}</td>}
                    <td className={td}>{r.planVersions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section
            title="Which airlines were on time?"
            caption="One row per airline. On time is the share of its flights within 15 minutes of schedule; each bar is that airline against the biggest figure in its column, so the outlier is seen first. Click a row to see that airline's flights."
          >
            <AirlinesTable
              airlines={d.airlines}
              config={config}
              onPick={(code) => {
                // The filter list carries codes upper-cased, whatever case the feed wrote them in.
                setAirline(code.toUpperCase());
                setPage("flights");
              }}
            />
          </Section>

          <Section title="Which stands were used most?" caption="One bar per stand, longest first, counted from each day's final plan. The number is how many aircraft visits the stand took.">
            <StandBars stands={d.stands} />
          </Section>
        </>
      )}
    </div>
  );
}

const SHEET_NAMES = { figures: "Figures", flights: "By flight", security: "Security", gates: "Gates", bags: "Bags", changes: "Changes" } as const;

/**
 * What an exported History tab says at its head, and the name the PDF is saved under:
 * which tab, and the dates it covers in words, so a sheet passed to another team is not
 * a page of figures with no period on it.
 */
export function historySheet(page: keyof typeof SHEET_NAMES, from: string, to: string, tz: string, iata: string): { title: string; detail: string[]; file: string } {
  const name = SHEET_NAMES[page];
  if (page === "changes") return { title: "History · Changes", detail: ["The change log as filtered on screen, newest first. Times are the airport's."], file: `${iata} history changes`.trim() };
  const a = DateTime.fromISO(from, { zone: tz });
  const b = DateTime.fromISO(to, { zone: tz });
  const days = Math.round(b.diff(a, "days").days) + 1;
  const range = from === to ? a.toFormat("cccc d LLLL yyyy") : `${a.toFormat("d LLL yyyy")} to ${b.toFormat("d LLL yyyy")} (${days} days)`;
  return { title: `History · ${name}`, detail: [range], file: `${iata} history ${name.toLowerCase()} ${from === to ? from : `${from} to ${to}`}`.trim() };
}

/** The Figures tab's How-to-read box: where each figure comes from and what a blank means. */
export function FiguresNotes() {
  return (
    <>
      <li>
        Flights, on-time shares and delays come from the flight feed's actual times, kept as each day ran. Passengers are the airline's own figure from its load reports.
        Through security is counted from the checkpoint's boarding-pass scans (BPSS2); the Security tab has the hour-by-hour picture.
      </li>
      <li>On time means within 15 minutes of schedule. It is green from 85%, amber from 70% and red below.</li>
      <li>A dash means nothing was measured for that figure, not zero.</li>
      <li>Stand use and plan changes come from each day's final plan in this app.</li>
      <li>Click an airline's row to open the By flight tab with only that airline.</li>
    </>
  );
}

/** The By flight tab's How-to-read box, with the fill scale drawn from the same bands the bars use. */
export function FlightsNotes() {
  return (
    <>
      <li>Each row is one flight number in one direction, added up over the dates chosen. Click the flight number to see it day by day.</li>
      <li>Through security is counted from the checkpoint's boarding-pass scans (BPSS2). Passengers is the airline's own figure from its load reports. Bags is from the bag system.</li>
      <li>
        A Through security reading that BPSS2's row limit cut short is left out, as the forecast leaves it out: on weekdays before 13 October 2026 the first departures read a
        handful of passengers, which was the read, not the flight.
      </li>
      <li>A dash means nothing was measured, not zero. A small &middot;1 after a figure means only one flight reported it.</li>
      <li>Per flight shows the average per flight flown; Range total adds the whole range up. Hover any figure to see both.</li>
      <li>
        How full is passengers against seats: the airline&apos;s figure, or for a departure without one the passengers through security, marked <i>security</i>. Seats marked * are for
        the type assumed from the airline and flight number, because the feed does not say what flew. Coloured on one scale: <FillScaleChips />
      </li>
    </>
  );
}

function Card({ label, value, sub, tone, onClick }: { label: string; value: string; sub?: string; tone?: string; onClick?: () => void }) {
  const body = (
    <>
      <div className="text-[11px] tracking-wide text-ink-3 uppercase">{label}</div>
      <div className={clsx("tabular text-[20px] font-bold", tone)}>{value}</div>
      {sub && <div className="text-[11px] text-ink-3">{sub}</div>}
    </>
  );
  // A card that opens a tab is a button, so it can be reached and pressed from the keyboard.
  return onClick ? (
    <button className="rounded-xl border border-line p-3 text-left hover:bg-panel-2/60" onClick={onClick} title="Open the Security tab">
      {body}
    </button>
  ) : (
    <div className="rounded-xl border border-line p-3">{body}</div>
  );
}

/** A small "?" beside a column word that is not enough on its own; the explanation is its hover. */
function Hint({ text }: { text: string }) {
  return (
    <span className="ml-1 inline-flex size-3.5 items-center justify-center rounded-full border border-line text-[9px] font-bold text-ink-3 normal-case" title={text} aria-label={text}>
      ?
    </span>
  );
}

/**
 * Every day in the range as one stacked bar, departures under arrivals in the two
 * direction colours, a red cap where anything was cancelled, and the on-time share as a
 * line against the right-hand scale. Which day went wrong is the first thing seen.
 */
export function DaysChart({ days, tz }: { days: DayReport[]; tz: string }) {
  const [at, setAt] = useState<string | null>(null);
  const shape = shapeDays(days);
  const hovered = shape.bars.find((b) => b.date === at) ?? null;
  const label = (date: string) => DateTime.fromISO(date, { zone: tz }).toFormat("ccc d LLL");
  const inset = shape.bars[0] ? shape.bars[0].w * 0.15 : 0;

  return (
    <div className={clsx(card, "mb-4")} onMouseLeave={() => setAt(null)}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[13px] font-semibold">Flights per day</div>
        <div className="text-[11.5px] text-ink-3">Hover a day for its figures</div>
      </div>

      <div className="flex gap-2">
        <div className="relative h-40 w-8 shrink-0">
          {shape.ticks.map((v) => (
            <div key={v} className="absolute right-0 -translate-y-1/2 text-[10px] text-ink-3" style={{ bottom: `${(v / shape.top) * 100}%` }}>
              {v}
            </div>
          ))}
        </div>

        <div className="relative flex-1">
          <div className="relative h-40 border-b border-line">
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
              {shape.ticks.map((v) => (
                <line key={v} x1={0} x2={100} y1={100 - (v / shape.top) * 100} y2={100 - (v / shape.top) * 100} className="stroke-line/60" strokeWidth={1} vectorEffect="non-scaling-stroke" />
              ))}
              {shape.bars.map((b) => (
                <g key={b.date} className={at === b.date ? "opacity-100" : "opacity-85"}>
                  {b.dep.h > 0 && <rect x={b.x + inset} y={b.dep.y} width={b.w - 2 * inset} height={b.dep.h} className="fill-ok" />}
                  {b.arr.h > 0 && <rect x={b.x + inset} y={b.arr.y} width={b.w - 2 * inset} height={b.arr.h} className="fill-info" />}
                  {b.cancelled && <rect x={b.x + inset} y={b.cancelled.y} width={b.w - 2 * inset} height={b.cancelled.h} className="fill-bad" />}
                </g>
              ))}
              {shape.onTime.length > 1 && (
                <polyline points={shape.onTime.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" className="stroke-ink" strokeWidth={2} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
              )}
              {/* A zero-length stroke with round caps is a dot that keeps its shape however the box is stretched. */}
              {shape.onTime.map((p) => (
                <polyline key={p.x} points={`${p.x},${p.y} ${p.x},${p.y}`} fill="none" className="stroke-ink" strokeWidth={5} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              ))}
            </svg>
            <div className="absolute inset-0 flex">
              {shape.bars.map((b) => (
                <button
                  key={b.date}
                  className="h-full flex-1 cursor-default"
                  onMouseEnter={() => setAt(b.date)}
                  onFocus={() => setAt(b.date)}
                  aria-label={`${label(b.date)}: ${b.day.flights} flights${b.day.onTimePct == null ? "" : `, ${b.day.onTimePct}% on time`}`}
                />
              ))}
            </div>
          </div>

          <div className="relative mt-1 h-4">
            {shape.bars
              .filter((_, i, all) => all.length <= 10 || i % Math.ceil(all.length / 10) === 0)
              .map((b) => (
                <span key={b.date} className="absolute -translate-x-1/2 text-[10px] text-ink-3" style={{ left: `${b.x + b.w / 2}%` }}>
                  {DateTime.fromISO(b.date, { zone: tz }).toFormat("d LLL")}
                </span>
              ))}
          </div>

          {hovered && (
            <div
              className="pointer-events-none absolute top-0 z-20 w-52 rounded-lg border border-line bg-panel p-2 text-[11.5px] shadow-lg"
              style={{ left: `min(max(0px, calc(${hovered.x + hovered.w / 2}% - 104px)), calc(100% - 208px))` }}
            >
              <div className="font-semibold">{label(hovered.date)}</div>
              <Readout label="Flights" value={num(hovered.day.flights)} />
              <Readout label="Arrivals" value={num(hovered.day.arrivals)} tone="text-info" />
              <Readout label="Departures" value={num(hovered.day.departures)} tone="text-ok" />
              <Readout label="Cancelled" value={num(hovered.day.cancelled)} tone={hovered.day.cancelled ? "text-bad" : undefined} />
              <Readout label="On time" value={pct(hovered.day.onTimePct)} tone={onTimeTone(hovered.day.onTimePct)} />
              <Readout label="Average delay" value={mins(hovered.day.avgDelayMin)} tone={delayTone(hovered.day.avgDelayMin)} />
              {hovered.day.screened != null && <Readout label="Through security" value={num(hovered.day.screened)} />}
            </div>
          )}
        </div>

        {/* The on-time scale, on the right so it is read as the line's and not the bars'. */}
        <div className="relative h-40 w-9 shrink-0 text-[10px] text-ink-3">
          {[0, 50, 100].map((v) => (
            <div key={v} className="absolute left-0 -translate-y-1/2" style={{ bottom: `${v}%` }}>
              {v}%
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-ink-3">
        <Key className="bg-info" label="arrivals" />
        <Key className="bg-ok" label="departures" />
        <Key className="bg-bad" label="cancelled" />
        <span className="flex items-center gap-1">
          <span className="inline-block h-0.5 w-4 rounded bg-ink" /> on time
        </span>
      </div>
    </div>
  );
}

function Readout({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="flex justify-between gap-3 text-ink-2">
      <span>{label}</span>
      <span className={clsx("tabular", tone ?? "text-ink")}>{value}</span>
    </div>
  );
}

function Key({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={clsx("inline-block size-2.5 rounded-sm", className)} /> {label}
    </span>
  );
}

type AirlineReport = { airline: string; flights: number; onTimePct: number | null; avgDelayMin: number | null; avgTurnMin: number | null; pax: number | null };

/**
 * One line per airline, each figure with a bar against the biggest in the table so the
 * eye finds the outlier before the number is read. A row opens that airline's flights.
 */
export function AirlinesTable({ airlines, config, onPick }: { airlines: AirlineReport[]; config: AirportConfig | undefined; onPick: (code: string) => void }) {
  const most = (of: (a: AirlineReport) => number | null) => Math.max(1, ...airlines.map((a) => of(a) ?? 0));
  const best = { onTime: most((a) => a.onTimePct), delay: most((a) => a.avgDelayMin), pax: most((a) => a.pax) };
  return (
    <table className="w-full max-w-3xl text-[12.5px]">
      <thead>
        <tr className="border-b border-line">
          <th className={th}>Airline</th>
          <th className={th} title="Arrivals and departures kept for the range">
            Flights flown
          </th>
          <th className={th} title="Share within 15 minutes of schedule">
            On time
          </th>
          <th className={th} title="Minutes late against schedule, averaged; an airline that ran early says early">
            Average delay
          </th>
          <th className={th} title="Minutes from arriving on stand to departing it, averaged">
            Time on stand
          </th>
          <th className={th} title="The airline's own figure, where a load report was received">
            Passengers
          </th>
        </tr>
      </thead>
      <tbody>
        {airlines.map((a) => (
          <tr key={a.airline} className="cursor-pointer border-b border-line hover:bg-panel-2/60" title="See this airline's flights" onClick={() => onPick(a.airline)}>
            <td className={td}>
              <AirlineMark code={a.airline} config={config} withName />
            </td>
            <td className={clsx(td, "tabular")}>{a.flights}</td>
            <td className={td}>
              <InlineBar text={pct(a.onTimePct)} value={a.onTimePct} max={best.onTime} tone={onTimeTone(a.onTimePct)?.replace("text-", "bg-") ?? "bg-ink-3"} />
            </td>
            <td className={td}>
              <InlineBar text={mins(a.avgDelayMin)} value={a.avgDelayMin} max={best.delay} tone={a.avgDelayMin != null && a.avgDelayMin > 10 ? "bg-warn" : "bg-ok"} />
            </td>
            <td className={clsx(td, "tabular")}>{mins(a.avgTurnMin)}</td>
            <td className={td}>
              <InlineBar text={num(a.pax)} value={a.pax} max={best.pax} tone="bg-info" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** A figure with a bar beside it; a figure below zero, such as an early average, draws no bar. */
function InlineBar({ text, value, max, tone }: { text: string; value: number | null; max: number; tone: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="tabular w-20 shrink-0">{text}</span>
      {value != null && (
        <span className="h-1.5 w-20 shrink-0 rounded-full bg-panel-2">
          <span className={clsx("block h-full rounded-full", tone)} style={{ width: `${Math.max(0, Math.min(100, (value / max) * 100))}%` }} />
        </span>
      )}
    </span>
  );
}

/** Stands by how many aircraft visits each took, longest first, the number at the end. */
export function StandBars({ stands }: { stands: Array<{ stand: string; turns: number }> }) {
  if (stands.length === 0) return <p className="text-[12.5px] text-ink-3">No plans were kept for these dates.</p>;
  const most = Math.max(1, ...stands.map((s) => s.turns));
  const sorted = [...stands].sort((a, b) => b.turns - a.turns || a.stand.localeCompare(b.stand, undefined, { numeric: true }));
  return (
    <div className="flex max-w-xl flex-col gap-1 text-[12.5px]">
      {sorted.map((s) => (
        <div key={s.stand} className="flex items-center gap-2">
          <span className="w-20 shrink-0 truncate font-semibold">{s.stand}</span>
          <span className="h-4 flex-1 rounded bg-panel-2">
            <span className="block h-full rounded bg-info/80" style={{ width: `${(s.turns / most) * 100}%` }} />
          </span>
          <span className="tabular w-16 shrink-0 text-ink-2">
            {s.turns} {s.turns === 1 ? "turn" : "turns"}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * A fill as a bar on the one scale, the percentage inside it. The label is drawn twice,
 * once in the page's ink and once in the panel's clipped to the filled part, so it reads
 * on the bar's colour and off it alike, in either theme, without a colour being guessed.
 */
export function FillBarView({ fill, className }: { fill: number; className?: string }) {
  const bar = fillBar(fill)!;
  const tone = fillTone(fill);
  return (
    <div className={clsx("relative h-4 overflow-hidden rounded bg-panel-2", className ?? "w-24")} title={`${tone.label}: airline passengers against seats; full width is 100%`}>
      <div className={clsx("h-full rounded", tone.bar)} style={{ width: `${bar.width}%` }} />
      <span className="tabular absolute inset-0 px-1.5 text-[11px] leading-4 font-semibold">{bar.label}</span>
      <span className="absolute inset-y-0 left-0 overflow-hidden text-panel" style={{ width: `${bar.width}%` }} aria-hidden>
        <span className="tabular block px-1.5 text-[11px] leading-4 font-semibold whitespace-nowrap">{bar.label}</span>
      </span>
    </div>
  );
}

/**
 * One row per airline on the table, fullest first, and a strip of where every flight
 * sits on the scale. Both are folded from the rows already on screen, so they follow the
 * filter and cost nothing to ask for.
 */
export function FillOverview({ rows, config, airline, onAirline }: { rows: FlightHistoryRow[]; config: AirportConfig | undefined; airline: string; onAirline: (code: string) => void }) {
  const byAirline = fillByAirline(rows);
  const counts = bandCounts(rows.map(rowFill));
  const measured = rows.length - counts.none;
  const bands = FILL_BANDS.filter((b) => b !== "over" || counts.over > 0);
  // The band's word and its edges as one phrase. "Over" is its own edge, so the legend
  // says "over 100%" once rather than "over over 100%".
  const legend = (b: FillBand) => (b === "over" ? "over 100%" : `${bandTone(b).label} ${bandRange(b)}`);

  return (
    <div className="mb-3 grid gap-3 lg:grid-cols-2">
      <div className={card}>
        <div className="mb-2 text-[13px] font-semibold">Fill by airline</div>
        <div className="mb-2 text-[11.5px] text-ink-3">Fullest first. Click an airline to show only its flights.</div>
        <div className="flex flex-col gap-1.5">
          {byAirline.map((a) => {
            const on = airline.toUpperCase() === a.airline.toUpperCase();
            return (
              <button
                key={a.airline}
                className={clsx("flex items-center gap-2 rounded-md px-1 py-0.5 text-left text-[12.5px] hover:bg-panel-2/60", on && "bg-panel-2")}
                title={on ? "Show every airline again" : `Show only ${a.name}`}
                aria-pressed={on}
                onClick={() => onAirline(on ? "" : a.airline)}
              >
                <span className="w-40 shrink-0 truncate">
                  <AirlineMark code={a.airline} config={config} withName />
                </span>
                {a.fill == null ? (
                  <span className="text-ink-3">not measured</span>
                ) : (
                  <>
                    <FillBarView fill={a.fill} className="w-28 sm:w-40" />
                    <span className="text-ink-3">
                      over {a.from} {a.from === 1 ? "flight" : "flights"}
                    </span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className={card}>
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <div className="text-[13px] font-semibold">Where the flights sit</div>
          <div className="text-[11.5px] text-ink-3">
            {measured} of {rows.length} measured
          </div>
        </div>
        <div className="mb-2 text-[11.5px] text-ink-3">How many flight numbers fall in each band of the scale.</div>
        {measured === 0 ? (
          <p className="text-[12.5px] text-ink-3">No flight here reported both passengers and seats.</p>
        ) : (
          <div className="flex h-6 overflow-hidden rounded" role="img" aria-label={bands.map((b) => `${counts[b]} ${bandTone(b).label}`).join(", ")}>
            {bands.map((b) => {
              const tone = bandTone(b);
              return (
                <div
                  key={b}
                  className={clsx("flex items-center justify-center text-[11px] font-semibold text-panel", tone.bar)}
                  style={{ width: `${(counts[b] / measured) * 100}%` }}
                  title={`${counts[b]} ${tone.label} (${bandRange(b)})`}
                >
                  {counts[b] > 0 && counts[b]}
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-ink-3">
          {bands.map((b) => (
            <Key key={b} className={bandTone(b).bar} label={legend(b)} />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * One row per flight number, so a flight can be compared with itself across days.
 *
 * Every measured column says how many operations it rests on, because that is the
 * difference between a fill of 94% and a fill of 94% worth acting on. Fuel and de-icing
 * have columns but no figures yet: Vectro Apron will fill them, and leaving the columns
 * visible is how anyone reading the table knows they are coming rather than missing.
 */
type SortKey = "flight" | "airline" | "seats" | "s2" | "pax" | "delay" | "bags" | "fill";

export function FlightTable({
  from,
  to,
  days,
  tz,
  config,
  airline,
  onAirline,
}: {
  from: string;
  to: string;
  /** Days in the range, for the opening sentence. */
  days: number;
  tz: string;
  config: AirportConfig | undefined;
  airline: string;
  onAirline: (code: string) => void;
}) {
  const [flight, setFlight] = useState("");
  const [direction, setDirection] = useState<"" | "ARR" | "DEP">("");
  const [each, setEach] = useState(true);
  const [sort, setSort] = useState<{ key: SortKey; down: boolean }>({ key: "flight", down: false });
  /** The row opened to its day-by-day chart, as "flight|direction". One at a time keeps the table a table. */
  const [open, setOpen] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["flight-history", from, to, airline, flight, direction],
    queryFn: () => api.flightHistory({ from, to, airline: airline || undefined, flight: flight.trim() || undefined, direction: direction || undefined }),
  });

  /** Per operation, or over the whole range: the reader's choice, not ours. */
  const val = (m: Measured) => (m.from === 0 ? null : each ? m.total / m.from : m.total);
  const summary = summarize(q.data?.rows ?? []);
  const avgTone = fillTone(summary.avgFill);

  const rows = [...(q.data?.rows ?? [])].sort((a, b) => {
    const of = (r: FlightHistoryRow): number | string | null => {
      switch (sort.key) {
        case "flight":
          return r.flight;
        case "airline":
          return r.airline;
        case "seats":
          return r.seats;
        case "s2":
          return val(r.s2);
        case "pax":
          return val(r.airlinePax);
        case "delay":
          return val(r.delay);
        case "bags":
          return val(r.bags);
        case "fill":
          return rowFill(r);
      }
    };
    const x = of(a);
    const y = of(b);
    // Rows with nothing measured sit at the bottom either way: absence is not a ranking.
    if (x == null && y == null) return a.flight.localeCompare(b.flight, undefined, { numeric: true });
    if (x == null) return 1;
    if (y == null) return -1;
    const cmp = typeof x === "string" ? x.localeCompare(String(y), undefined, { numeric: true }) : x - Number(y);
    return sort.down ? -cmp : cmp;
  });

  /**
   * A sortable column heading. The hover title explains the word; `ask` adds a small "?"
   * for the columns where the word alone is not enough, which is most of the measured
   * ones and none of the obvious ones.
   */
  const head = (key: SortKey, label: string, hint?: string, ask = false) => (
    <th
      className={clsx(th, "cursor-pointer select-none hover:text-ink")}
      title={hint}
      onClick={() => setSort((s) => ({ key, down: s.key === key ? !s.down : true }))}
    >
      {label}
      {ask && hint && <Hint text={hint} />}
      {sort.key === key && <span className="ml-0.5 text-ink-3">{sort.down ? "▾" : "▴"}</span>}
    </th>
  );

  /** A figure, with what it was measured across kept one hover away rather than dropped. */
  const figure = (m: Measured, digits = 0) => {
    const v = val(m);
    if (v == null) return <span className="text-ink-3">&mdash;</span>;
    return (
      <span title={`${m.total.toLocaleString()} over ${m.from} ${m.from === 1 ? "flight" : "flights"}`}>
        {v.toLocaleString(undefined, { maximumFractionDigits: digits })}
        {m.from === 1 && (
          <span className="ml-0.5 text-ink-3" title="Only one flight reported this">
            &middot;1
          </span>
        )}
      </span>
    );
  };

  const download = () => {
    const cols = ["Airline", "Flight", "Direction", "Operations", "Cancelled", "PaxCapacity", "S2", "AirlinePAX", "DelayMin", "Bags", "FillPct", "FuelKg", "DeiceMin"];
    const lines = [
      cols.join(","),
      ...rows.map((r) =>
        [
          r.airline,
          r.flight,
          r.direction,
          r.operations,
          r.cancelled,
          r.seats ?? "",
          val(r.s2)?.toFixed(0) ?? "",
          val(r.airlinePax)?.toFixed(0) ?? "",
          val(r.delay)?.toFixed(1) ?? "",
          val(r.bags)?.toFixed(0) ?? "",
          rowFill(r)?.toFixed(1) ?? "",
          "",
          "",
        ].join(","),
      ),
    ];
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `flights-${from}-to-${to}${each ? "-per-flight" : "-totals"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <HowToRead id="history.flights">
        <FlightsNotes />
      </HowToRead>

      <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px]">
        <select className={input} value={airline} onChange={(e) => onAirline(e.target.value)}>
          <option value="">All airlines</option>
          {(q.data?.airlines ?? []).map((a) => (
            <option key={a.code} value={a.code}>
              {a.name}
              {a.iata ? ` (${a.iata})` : ""}
            </option>
          ))}
          {/* An airline chosen on the Figures tab is on the list even before its rows arrive. */}
          {airline && !(q.data?.airlines ?? []).some((a) => a.code === airline) && <option value={airline}>{airline}</option>}
        </select>
        <input className={clsx(input, "w-32")} placeholder="Flight number" value={flight} onChange={(e) => setFlight(e.target.value)} />
        <select className={input} value={direction} onChange={(e) => setDirection(e.target.value as "" | "ARR" | "DEP")}>
          <option value="">Arrivals and departures</option>
          <option value="ARR">Arrivals</option>
          <option value="DEP">Departures</option>
        </select>
        <div className="flex overflow-hidden rounded-md border border-line" title="Per flight: the average per flight flown. Range total: everything in the range added up.">
          <button className={clsx("px-2 py-1", each ? "bg-panel-2 font-semibold" : "text-ink-2")} onClick={() => setEach(true)}>
            Per flight
          </button>
          <button className={clsx("px-2 py-1", !each ? "bg-panel-2 font-semibold" : "text-ink-2")} onClick={() => setEach(false)}>
            Range total
          </button>
        </div>
        {q.isFetching && <Loader2 className="size-4 animate-spin text-ink-3" />}
        <button className="ml-auto rounded-md border border-line px-2 py-1 text-ink-2 hover:text-ink disabled:opacity-40 print:hidden" onClick={download} disabled={!rows.length}>
          Download CSV
        </button>
      </div>

      {q.data && <p className="mb-4 max-w-3xl text-[14px] text-ink">{flightsSentence(q.data.rows, days)}</p>}

      {q.data && rows.length > 0 && (
        <>
          <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Card label="Flight numbers" value={num(summary.flights)} sub="each direction its own row" />
            <Card label="Flights flown" value={num(summary.operations)} sub={summary.cancelled ? `plus ${summary.cancelled} cancelled` : "cancellations excluded"} />
            <Card
              label="How full"
              value={summary.avgFill == null ? "—" : `${Math.round(summary.avgFill)}%`}
              tone={summary.avgFill == null ? undefined : avgTone.text}
              sub={summary.fillFrom ? `${avgTone.label} · passengers against seats over ${summary.fillFrom} flights` : "no flight reported both passengers and seats"}
            />
            <Card label="Through security" value={num(summary.s2)} sub={summary.s2From ? `counted on ${summary.s2From} departures` : "no departure counted"} />
          </div>

          <Section
            title="How full were the flights?"
            caption="Passengers (the airline's figure) against seats, coloured on one five-band scale. One panel per airline, fullest first, and one strip of how many flight numbers fall in each band."
          >
            <FillOverview rows={q.data.rows} config={config} airline={airline} onAirline={onAirline} />
          </Section>
        </>
      )}

      {q.data && rows.length > 0 && (
        <div className="mb-2">
          <h2 className="text-[14px] font-bold">Where does each flight number sit?</h2>
          <p className="max-w-3xl text-[12px] text-ink-3">
            One row per flight number and direction, added up over the range. Click a flight number for its day-by-day chart; click a column heading to sort by it.
          </p>
        </div>
      )}

      <div className="scroll-thin overflow-x-auto">
        <table className="w-full min-w-[900px] text-[12.5px]">
          <thead>
            <tr className="border-b border-line">
              {head("airline", "Airline")}
              {head("flight", "Flight")}
              <th className={th} title="Arrival or departure. They are separate rows because they are separate flights.">
                Direction
              </th>
              <th className={th} title="How many times it ran in the range. Cancellations are shown beside it and left out of every other figure.">
                Flights flown
              </th>
              {head("seats", "Seats", "Seats on the aircraft type it usually flies; hover a figure for the type")}
              {head("s2", "Through security", "Passengers counted through the security checkpoint from boarding-pass scans. Departures only.", true)}
              {head("pax", "Passengers (airline's figure)", "The airline's own passenger count, from its load report", true)}
              {head("delay", "Minutes late (negative = early)", "Minutes against schedule, averaged; a figure below zero means it ran early")}
              {head("bags", "Bags", "From the furthest point down the bag line that reported: loaded, else made up, else screened, else checked in", true)}
              {head("fill", "How full", "Passengers (airline's figure) against seats; full width of the bar is every seat taken", true)}
              <th className={th} title="Measured on the apron. Vectro Apron fills this once connected.">
                Fuel
              </th>
              <th className={th} title="Measured on the apron. Vectro Apron fills this once connected.">
                De-ice
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const delay = val(r.delay);
              const fill = rowFill(r);
              const key = `${r.flight}|${r.direction}`;
              const opened = open === key;
              return (
                <Fragment key={key}>
                  <tr className={clsx("tabular border-b border-line", opened && "bg-panel-2/60")}>
                    <td className={td} title={`${r.airlineName}${r.iata ? ` (${r.iata})` : ""}`}>
                      <span className="inline-flex items-center gap-1.5">
                        <AirlineMark code={r.airline} config={config} />
                        {r.iata ?? r.airline}
                      </span>
                    </td>
                    <td className={clsx(td, "font-semibold")}>
                      <button
                        className="flex items-center gap-1 hover:underline"
                        title={opened ? "Hide the day-by-day chart" : "Show this flight day by day"}
                        aria-expanded={opened}
                        onClick={() => setOpen(opened ? null : key)}
                      >
                        {r.flight}
                        <ChevronDown className={clsx("size-3.5 text-ink-3 transition-transform", opened && "rotate-180")} />
                      </button>
                    </td>
                    <td className={td}>
                      <Direction dir={r.direction} withWord />
                    </td>
                    <td className={clsx(td, "text-ink-2")}>
                      {r.operations}
                      {r.cancelled > 0 && (
                        <span className="text-bad" title={`${r.cancelled} cancelled, left out of the other figures`}>
                          {" "}
                          +{r.cancelled} cancelled
                        </span>
                      )}
                    </td>
                    <td className={td} title={r.aircraftType ? `${r.aircraftType}${r.aircraftTypeAssumed ? ", assumed from the airline and flight number: the feed does not say what flew" : ""}` : undefined}>
                      {r.seats ?? <span className="text-ink-3">&mdash;</span>}
                      {r.seats != null && r.aircraftTypeAssumed && <span className="text-ink-3">*</span>}
                    </td>
                    <td className={td}>
                      {r.direction === "DEP" ? (
                        figure(r.s2)
                      ) : (
                        <span className="text-ink-3" title="Arriving passengers do not go through security here">
                          n/a
                        </span>
                      )}
                    </td>
                    <td className={td}>{figure(r.airlinePax)}</td>
                    <td className={td}>
                      {delay == null ? (
                        <span className="text-ink-3">&mdash;</span>
                      ) : (
                        <span className={delay > 15 ? "text-bad" : delay < -5 ? "text-ink-2" : ""} title={`${r.delay.from} measured`}>
                          {delay > 0 ? "+" : ""}
                          {delay.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                        </span>
                      )}
                    </td>
                    <td className={td}>{figure(r.bags)}</td>
                    <td className={td}>
                      {fill == null ? (
                        <span className="text-ink-3">&mdash;</span>
                      ) : (
                        <span className="flex items-center gap-1" title={rowFillBasis(r) === "security" ? "No airline passenger figure: passengers through security against seats, which can only be lower than the real load" : undefined}>
                          <FillBarView fill={fill} />
                          {rowFillBasis(r) === "security" && <span className="text-[10.5px] text-ink-3">security</span>}
                        </span>
                      )}
                    </td>
                    <td className={clsx(td, "text-ink-3")}>&mdash;</td>
                    <td className={clsx(td, "text-ink-3")}>&mdash;</td>
                  </tr>
                  {opened && (
                    <tr className="border-b border-line">
                      <td colSpan={12} className="px-3 pb-2">
                        <FlightSeriesRow from={from} to={to} flight={r.flight} direction={r.direction} tz={tz} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 max-w-2xl text-[11.5px] text-ink-3">
        A dash means nothing was measured, not zero. Fuel and de-icing are measured on the apron, not here. The columns stay so it is clear they are coming rather
        than missing: Vectro Apron fills them once it is connected.
      </p>
    </>
  );
}

/** The chart under an opened row: fetched when opened, so the table itself stays one request. */
function FlightSeriesRow({ from, to, flight, direction, tz }: { from: string; to: string; flight: string; direction: "ARR" | "DEP"; tz: string }) {
  const q = useQuery({ queryKey: ["flight-series", from, to, flight, direction], queryFn: () => api.flightSeries({ from, to, flight, direction }) });
  if (!q.data) return <Loader2 className="my-2 size-4 animate-spin text-ink-3" />;
  return <FlightSeriesChart series={q.data} tz={tz} />;
}
